import React from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useTheme, Theme } from '../contexts/ThemeContext';

const Settings: React.FC = () => {
  const { isAuthenticated, user } = useAuth0();
  const { currentTheme, setTheme, availableThemes } = useTheme();

  if (!isAuthenticated) {
    return (
      <div className="container">
        <p>Please log in to access settings.</p>
      </div>
    );
  }

  const handleThemeChange = (themeId: string) => {
    setTheme(themeId);
  };

  const getThemePreview = (theme: Theme) => (
    <div style={{
      display: 'flex',
      gap: '4px',
      alignItems: 'center'
    }}>
      <div style={{
        width: '16px',
        height: '16px',
        borderRadius: '50%',
        backgroundColor: theme.colors.primary,
        border: '2px solid white',
        boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
      }} />
      <div style={{
        width: '16px',
        height: '16px',
        borderRadius: '50%',
        backgroundColor: theme.colors.accent,
        border: '2px solid white',
        boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
      }} />
      <div style={{
        width: '16px',
        height: '16px',
        borderRadius: '50%',
        backgroundColor: theme.colors.header,
        border: '2px solid white',
        boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
      }} />
    </div>
  );

  return (
    <div className="container">
      <div style={{ marginBottom: '2rem' }}>
        <h2>Settings</h2>
        <p style={{ color: 'var(--color-textSecondary)' }}>
          Customize your Storage Tank Platform experience
        </p>
      </div>

      {/* User Profile Section */}
      <div style={{
        backgroundColor: 'var(--color-surface)',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--border-radius-medium)',
        padding: '2rem',
        marginBottom: '2rem',
        boxShadow: 'var(--shadow-card)'
      }}>
        <h3 style={{ marginTop: 0, color: 'var(--color-text)' }}>Profile Information</h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
          <div style={{
            width: '60px',
            height: '60px',
            borderRadius: '50%',
            backgroundColor: 'var(--color-primary)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '1.5rem',
            color: 'white'
          }}>
            👤
          </div>
          <div>
            <h4 style={{ margin: 0, color: 'var(--color-text)' }}>{user?.name}</h4>
            <p style={{ margin: '0.25rem 0 0 0', color: 'var(--color-textSecondary)' }}>{user?.email}</p>
          </div>
        </div>
      </div>

      {/* Theme Selection Section */}
      <div style={{
        backgroundColor: 'var(--color-surface)',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--border-radius-medium)',
        padding: '2rem',
        boxShadow: 'var(--shadow-card)'
      }}>
        <h3 style={{ marginTop: 0, color: 'var(--color-text)' }}>Theme Selection</h3>
        <p style={{ color: 'var(--color-textSecondary)', marginBottom: '1.5rem' }}>
          Choose a theme that matches your style and work environment
        </p>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: '1rem'
        }}>
          {availableThemes.map((theme) => (
            <div
              key={theme.id}
              onClick={() => handleThemeChange(theme.id)}
              style={{
                border: `2px solid ${currentTheme.id === theme.id ? 'var(--color-primary)' : 'var(--color-border)'}`,
                borderRadius: 'var(--border-radius-medium)',
                padding: '1rem',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                backgroundColor: currentTheme.id === theme.id ? 'var(--color-primary)' : 'var(--color-surface)',
                color: currentTheme.id === theme.id ? 'white' : 'var(--color-text)',
                position: 'relative' as const,
                overflow: 'hidden'
              }}
              onMouseEnter={(e) => {
                if (currentTheme.id !== theme.id) {
                  e.currentTarget.style.borderColor = 'var(--color-accent)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = 'var(--shadow-elevated)';
                }
              }}
              onMouseLeave={(e) => {
                if (currentTheme.id !== theme.id) {
                  e.currentTarget.style.borderColor = 'var(--color-border)';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }
              }}
            >
              {currentTheme.id === theme.id && (
                <div style={{
                  position: 'absolute',
                  top: '0.5rem',
                  right: '0.5rem',
                  fontSize: '1.2rem'
                }}>
                  ✓
                </div>
              )}
              
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
                <div>
                  <h4 style={{ 
                    margin: '0 0 0.25rem 0', 
                    fontSize: '1.1rem',
                    color: currentTheme.id === theme.id ? 'white' : 'var(--color-text)'
                  }}>
                    {theme.name}
                  </h4>
                  <p style={{ 
                    margin: 0, 
                    fontSize: '0.9rem',
                    color: currentTheme.id === theme.id ? 'rgba(255,255,255,0.9)' : 'var(--color-textSecondary)'
                  }}>
                    {theme.description}
                  </p>
                </div>
                {getThemePreview(theme)}
              </div>

              {/* Theme color preview bar */}
              <div style={{
                height: '4px',
                borderRadius: '2px',
                background: `linear-gradient(90deg, ${theme.colors.primary}, ${theme.colors.accent}, ${theme.colors.success})`,
                marginTop: '0.75rem'
              }} />
            </div>
          ))}
        </div>

        <div style={{
          marginTop: '2rem',
          padding: '1rem',
          backgroundColor: 'var(--color-background)',
          borderRadius: 'var(--border-radius-small)',
          border: '1px solid var(--color-border)'
        }}>
          <h4 style={{ margin: '0 0 0.5rem 0', color: 'var(--color-text)' }}>
            Current Theme: {currentTheme.name}
          </h4>
          <p style={{ margin: 0, fontSize: '0.9rem', color: 'var(--color-textSecondary)' }}>
            {currentTheme.description}
          </p>
        </div>
      </div>

      {/* Additional Settings Placeholder */}
      <div style={{
        backgroundColor: 'var(--color-surface)',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--border-radius-medium)',
        padding: '2rem',
        marginTop: '2rem',
        boxShadow: 'var(--shadow-card)'
      }}>
        <h3 style={{ marginTop: 0, color: 'var(--color-text)' }}>Additional Settings</h3>
        <div style={{
          display: 'grid',
          gap: '1rem'
        }}>
          <label style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            color: 'var(--color-text)',
            cursor: 'pointer'
          }}>
            <input
              type="checkbox"
              defaultChecked={false}
              style={{
                accentColor: 'var(--color-primary)'
              }}
            />
            Enable email notifications for facility updates
          </label>
          
          <label style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            color: 'var(--color-text)',
            cursor: 'pointer'
          }}>
            <input
              type="checkbox"
              defaultChecked={true}
              style={{
                accentColor: 'var(--color-primary)'
              }}
            />
            Show map markers for all search results
          </label>
          
          <label style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            color: 'var(--color-text)',
            cursor: 'pointer'
          }}>
            <input
              type="checkbox"
              defaultChecked={true}
              style={{
                accentColor: 'var(--color-primary)'
              }}
            />
            Enable high-risk facility alerts
          </label>
        </div>
      </div>
    </div>
  );
};

export default Settings;